/**
 * Tela de entrada.
 *
 * O painel de marca, o cartão e a regra de caber sem rolagem moram em `TelaDeAcesso`, dividida
 * com a recuperação de senha. Aqui fica só o que é de entrar: o texto da esquerda e o formulário.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Fade,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  MailOutline,
  LockOutlined,
  DescriptionOutlined,
  GroupsOutlined,
  InsightsOutlined,
  ShieldOutlined,
  NorthEast,
} from '@mui/icons-material';

import { useAuth } from '../stores/authStore';
import branding from '../config/branding';
import { authService } from '../services/authService';
import { LoginCredentials } from '../types/auth';
import { TelaDeAcesso, type ApoioDaHistoria } from '../components/auth/TelaDeAcesso';
import {
  estiloDoBotao,
  estiloDoCampo,
  loginAccent,
  LINHA,
  SERIFA,
  TEAL_LINK,
  TINTA,
  TINTA_SUAVE,
} from '../components/auth/estilos';

const DIFERENCIAIS: readonly ApoioDaHistoria[] = [
  {
    chave: 'esocial',
    marca: <DescriptionOutlined />,
    titulo: 'eSocial integrado',
    texto: 'Envio automático e com segurança.',
  },
  {
    chave: 'gestao',
    marca: <GroupsOutlined />,
    titulo: 'Gestão ocupacional centralizada',
    texto: 'Todas as informações em um só lugar.',
  },
  {
    chave: 'controle',
    marca: <InsightsOutlined />,
    titulo: 'Mais controle. Menos retrabalho.',
    texto: 'Processos ágeis e equipes mais produtivas.',
  },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, logout, isAuthenticated, isLoading, error, clearError, mfaPending } = useAuth();

  // Link de e-mail (boas-vindas, redefinição de senha): quem abre pode estar com outra
  // conta logada neste mesmo navegador. Sem isso, o redirecionamento abaixo pula a tela de
  // login e leva pra sessão que já estava ativa, em vez da conta que o link é sobre.
  const forcarNovoLogin = searchParams.get('forcarNovoLogin') === '1';
  const logoutDisparado = useRef(false);

  // Form state - initialize with remembered email if available
  const [formData, setFormData] = useState<LoginCredentials>(() => {
    if (authService.getRememberMe()) {
      const rememberedEmail = authService.getRememberedEmail();
      if (rememberedEmail) {
        return {
          email: rememberedEmail,
          password: '',
          rememberMe: true,
        };
      }
    }
    return {
      email: '',
      password: '',
      rememberMe: false,
    };
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Get redirect path from location state
  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/home';

  // Redirect if already authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    if (forcarNovoLogin) {
      if (!logoutDisparado.current) {
        logoutDisparado.current = true;
        void logout();
      }
      return;
    }

    navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from, forcarNovoLogin, logout]);

  // Redirect to MFA verify page when MFA is pending
  useEffect(() => {
    if (mfaPending) {
      navigate('/mfa-verify', { replace: true, state: { from: { pathname: from } } });
    }
  }, [mfaPending, navigate, from]);

  // Clear error when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [formData, clearError]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'E-mail inválido';
    }

    if (!formData.password) {
      errors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      errors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await login(formData);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleInputChange = (field: keyof LoginCredentials) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'rememberMe' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <TelaDeAcesso
      titulo="SST mais simples."
      destaque="Gestão mais inteligente."
      subtitulo="Conformidade, saúde ocupacional e engajamento em uma única plataforma."
      apoios={DIFERENCIAIS}
      selo={['Conformidade hoje.', 'Um amanhã mais seguro.']}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          component="h2"
          sx={{
            fontFamily: SERIFA,
            fontWeight: 700,
            // Tamanho fixo de propósito: o cartão tem ~310px de texto útil, e é exatamente
            // onde "Bem-vindo de volta" cabe em uma linha. Escalar com a tela quebra o
            // título em duas em monitor grande, que é o oposto do que a fórmula promete.
            fontSize: { xs: '1.7rem', md: '2rem' },
            letterSpacing: '-0.02em',
            color: TINTA,
          }}
        >
          Bem-vindo de volta
        </Typography>
        <Typography
          sx={{
            mt: 0.75,
            mb: 'clamp(16px, 2.8vh, 30px)',
            color: TINTA_SUAVE,
            fontSize: '1.02rem',
          }}
        >
          Acesse sua conta para continuar.
        </Typography>
      </Box>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={clearError}>
            {error}
          </Alert>
        </Fade>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography
          component="label"
          htmlFor="email"
          sx={{ display: 'block', mb: 0.875, fontWeight: 700, color: TINTA }}
        >
          E-mail
        </Typography>
        <TextField
          fullWidth
          id="email"
          name="email"
          type="email"
          placeholder="seu@email.com"
          value={formData.email}
          onChange={handleInputChange('email')}
          error={!!formErrors.email}
          helperText={formErrors.email}
          disabled={isLoading}
          autoComplete="email"
          autoFocus
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutline sx={{ color: '#71809a' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={estiloDoCampo}
        />

        <Typography
          component="label"
          htmlFor="password"
          sx={{
            display: 'block',
            mt: 'clamp(12px, 2.2vh, 20px)',
            mb: 0.875,
            fontWeight: 700,
            color: TINTA,
          }}
        >
          Senha
        </Typography>
        <TextField
          fullWidth
          id="password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          placeholder="Sua senha"
          value={formData.password}
          onChange={handleInputChange('password')}
          error={!!formErrors.password}
          helperText={formErrors.password}
          disabled={isLoading}
          autoComplete="current-password"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined sx={{ color: '#71809a' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={isLoading}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    sx={{ color: '#71809a' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={estiloDoCampo}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mt: 'clamp(10px, 1.8vh, 18px)',
            mb: 'clamp(14px, 2.6vh, 26px)',
          }}
        >
          <FormControlLabel
            sx={{ m: 0, mr: 0 }}
            control={
              <Checkbox
                checked={formData.rememberMe}
                onChange={handleInputChange('rememberMe')}
                disabled={isLoading}
                size="small"
                sx={{ p: 0.5, mr: 0.5, color: '#b6c2d4', '&.Mui-checked': { color: loginAccent } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.92rem', whiteSpace: 'nowrap', color: TINTA_SUAVE }}>
                Lembrar de mim
              </Typography>
            }
          />
          <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
            <Typography
              sx={{
                fontSize: '0.92rem',
                whiteSpace: 'nowrap',
                color: TEAL_LINK,
                '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
              }}
            >
              Esqueci minha senha
            </Typography>
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          disabled={isLoading}
          endIcon={isLoading ? undefined : <NorthEast sx={{ fontSize: 18 }} />}
          sx={estiloDoBotao}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Entrar na plataforma'}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          my: 'clamp(14px, 2.6vh, 26px)',
          color: TINTA_SUAVE,
          fontSize: '0.9rem',
          '&::before, &::after': {
            content: '""',
            flex: 1,
            height: '1px',
            bgcolor: LINHA,
          },
        }}
      >
        ou
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ mb: 0.75, fontWeight: 700, color: TINTA }}>
          Precisa de ajuda para acessar?
        </Typography>
        <Typography
          component="a"
          href={`mailto:${branding.supportEmail}`}
          sx={{
            fontWeight: 700,
            color: TEAL_LINK,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
          }}
        >
          Fale com o suporte →
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mt: 'clamp(14px, 2.6vh, 26px)',
          color: TINTA_SUAVE,
          fontSize: '0.86rem',
          textAlign: 'center',
        }}
      >
        <ShieldOutlined sx={{ fontSize: 20, color: loginAccent }} />
        <span>O acesso é concluído no ambiente oficial e seguro da {branding.appName}.</span>
      </Box>
    </TelaDeAcesso>
  );
};

export default LoginPage;
